import { MedicalType } from "@qendo/medical/types";
import type { Section } from "@qendo/permissions";
import { Stack } from "expo-router";
import { useState } from "react";
import { Loading } from "~/components/feedback/Loading";
import { LoadingError } from "~/components/feedback/LoadingError";
import { BloodTypeForm } from "~/components/form/health-profile/BloodTypeForm";
import { MedicalListingItem } from "~/components/form/health-profile/MedicalListingItem";
import { ListingGroup } from "~/components/layout/ListingGroup";
import { ListingItem } from "~/components/layout/ListingItem";
import { Page } from "~/components/layout/Page";
import { SectionHeader } from "~/components/layout/SectionHeader";
import { Authorize } from "~/components/permission/Authorize";
import { Can } from "~/components/permission/Can";
import { CanAny } from "~/components/permission/CanAny";
import { ShareButton } from "~/components/ShareButton";
import { useMedical } from "~/providers/MedicalProvider";
import { useProfile } from "~/providers/ProfileProvider";

export const HEALTH_PROFILE_SECTIONS: Section[] = [
    "profile",
    "documents",
    "emergency",
    "medical",
    "allergies",
    "conditions",
    "immunisations",
    "medications",
    "past_symptoms",
    "supplements",
    "surgeries",
    "triggers",
];

export default function HealthProfile() {
    const { refreshMedicalCache, loadingMedical, loadingMedicalError } = useMedical();
    const { profile, can, cannot, cannotAny } = useProfile();
    const [editBloodType, setEditBloodType] = useState(false);
    const can_edit_blood_type = can("update", ["medical", "profile"]);

    return (
        <>
            <Stack.Screen
                options={{
                    headerRight: () => (
                        <ShareButton
                            title="Share health profile"
                            initial={["profile", "medical", "photos", "documents"]}
                        />
                    ),
                }}
            />
            <Authorize ability="view" any section={HEALTH_PROFILE_SECTIONS}>
                <Page spacer refreshWith={refreshMedicalCache}>
                    <SectionHeader title="Health profile" icon="medical-profile">
                        Organise your health information in one place and easily share key details with your care team.
                    </SectionHeader>
                    {loadingMedical && <Loading />}
                    {loadingMedicalError && <LoadingError retry={refreshMedicalCache} />}
                    <CanAny section={["documents", "emergency"]} ability="view">
                        <ListingGroup>
                            <Can ability="view" section="emergency">
                                <ListingItem
                                    href="/health-profile/emergency-instructions"
                                    icon="ambulance"
                                    title="Emergency instructions"
                                    last={cannot("view", "documents")}
                                />
                            </Can>
                            <Can ability="view" section="documents">
                                <ListingItem
                                    icon="prescribed-file"
                                    title="Prescribed pain plan"
                                    href="/health-profile/pain-plan"
                                    last
                                />
                            </Can>
                        </ListingGroup>
                    </CanAny>
                    <CanAny section={["medications", "supplements"]} ability="view">
                        <ListingGroup>
                            <Can ability="view" section="medications">
                                <MedicalListingItem
                                    type={MedicalType.medication}
                                    title="Medications"
                                    last={cannot("view", "supplements")}
                                />
                            </Can>
                            <Can ability="view" section="supplements">
                                <MedicalListingItem type={MedicalType.supplement} title="Supplements" last />
                            </Can>
                        </ListingGroup>
                    </CanAny>
                    <CanAny section={["allergies", "conditions", "triggers", "symptoms"]} ability="view">
                        <ListingGroup>
                            <Can section="conditions" ability="view">
                                <MedicalListingItem
                                    type={MedicalType.condition}
                                    title="Conditions"
                                    last={cannotAny("view", ["triggers", "symptoms", "allergies"])}
                                />
                            </Can>
                            <Can section="triggers" ability="view">
                                <MedicalListingItem
                                    type={MedicalType.trigger}
                                    title="Triggers"
                                    last={cannotAny("view", ["symptoms", "allergies"])}
                                />
                            </Can>
                            <Can section="past_symptoms" ability="view">
                                <MedicalListingItem
                                    type={MedicalType.past_symptom}
                                    title="Past symptoms"
                                    last={cannot("view", "allergies")}
                                />
                            </Can>
                            <Can section="allergies" ability="view">
                                <MedicalListingItem type={MedicalType.allergy} title="Allergies" last />
                            </Can>
                        </ListingGroup>
                    </CanAny>
                    <CanAny section={["documents", "immunisations", "profile", "surgeries"]} ability="view">
                        <ListingGroup>
                            <Can section="immunisations" ability="view">
                                <MedicalListingItem
                                    type={MedicalType.immunisation}
                                    title="Immunisations"
                                    last={cannotAny("view", ["documents", "surgeries", "profile"])}
                                />
                            </Can>
                            <Can section="documents" ability="view">
                                <ListingItem
                                    icon="file"
                                    title="Clinical documents"
                                    href="/health-profile/clinical-documents"
                                />
                            </Can>
                            <Can section="surgeries" ability="view">
                                <MedicalListingItem
                                    type={MedicalType.surgery}
                                    title="Surgeries and interventions"
                                    last={cannot("view", "profile")}
                                />
                            </Can>
                            <Can section="profile" ability="view">
                                <ListingItem
                                    icon="drop"
                                    title="Blood type"
                                    subtitle={profile?.blood_type}
                                    onPress={() => can_edit_blood_type && setEditBloodType(true)}
                                    hideChevron={!can_edit_blood_type}
                                    last
                                />
                            </Can>
                        </ListingGroup>
                    </CanAny>
                </Page>
                {can_edit_blood_type && editBloodType && (
                    <BloodTypeForm onRequestClose={() => setEditBloodType(false)} />
                )}
            </Authorize>
        </>
    );
}
